<template>
  <div
    v-if="hasTeasers"
    class="sustainable-products"
  >
    <Heading
      class="sustainable-products__heading"
      tag="h2"
      size="md"
      font-weight="regular"
      spaces-bottom-rem
    >
      Nachhaltige Produkte
    </Heading>
    <div class="sustainable-products__list">
      <SustainableTeaser :teaser="teaser1">
        <template #mouseOver="{ mouseOver }">
          <CdnImage
            class="sustainable-teaser__element leaf-2"
            :class="{
              'sustainable-teaser--move-left': mouseOver,
              'sustainable-teaser--rotate-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/94fb578a1c96ef15b23b3940a26bd1d1/leaf-2.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="169"
            :height="109"
          />
          <CdnImage
            class="sustainable-teaser__element leaf-1"
            :class="{
              'sustainable-teaser--move-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/757d70b6039dcda52a6ab5c07ebed48a/leaf-1.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="93"
            :height="122"
          />
          <CdnImage
            class="sustainable-teaser__element leaf-3"
            :class="{
              'sustainable-teaser--move-right': mouseOver,
              'sustainable-teaser--rotate-right': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/49574789550c4cec1f5b1321a276b3d3/leaf-3.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="154"
            :height="193"
          />
        </template>
      </SustainableTeaser>

      <SustainableTeaser
        :teaser="teaser2"
        variant="dark"
      >
        <template #mouseOver="{ mouseOver }">
          <CdnImage
            class="sustainable-teaser__element leaf-4"
            :class="{
              'sustainable-teaser--move-left': mouseOver,
              'sustainable-teaser--rotate-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/3e36f78e96d6b8aa34c9044bf27ace1c/leaf-4.svg"
            aria-hidden="true"
            render-without-figure
            lazy
            :width="167"
            :height="184"
          />
          <CdnImage
            class="sustainable-teaser__element leaf-5"
            :class="{
              'sustainable-teaser--move-right': mouseOver,
              'sustainable-teaser--rotate-right': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/9c1adb76632e018a58386be330a11c27/leaf-5.svg"
            alt=""
            aria-hidden="true"
            render-without-figure
            lazy
            :width="239"
            :height="273"
          />
        </template>
        <CdnImage
          class="sustainable-teaser__container-badge"
          src="/content/content-images/homepage/sustainable-products/bc42f372014c0e2ec4df6e86c82cfa6b/green-magenta-badge.svg"
          alt="#Green Magenta"
          render-without-figure
          lazy
        />
      </SustainableTeaser>

      <SustainableTeaser
        :teaser="teaser3"
        variant="light"
      >
        <template #mouseOver="{ mouseOver }">
          <CdnImage
            class="sustainable-teaser__element leaf-6"
            :class="{
              'sustainable-teaser--move-left': mouseOver,
              'sustainable-teaser--rotate-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/6b6f9be6b26e9de592033f830ba1ebe0/leaf-6.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="206"
            :height="196"
          />
          <CdnImage
            class="sustainable-teaser__element leaf-7"
            :class="{
              'sustainable-teaser--move-right': mouseOver,
              'sustainable-teaser--rotate-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/57b5395dea429aebfa254dc5aabf5fd9/leaf-7.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="202"
            :height="142"
          />
          <CdnImage
            class="sustainable-teaser__element leaf-8"
            :class="{
              'sustainable-teaser--move-right': mouseOver,
              'sustainable-teaser--rotate-left': mouseOver,
            }"
            src="/content/content-images/homepage/sustainable-products/a8059e592deccd252f5ba26f2151d8c3/leaf-8.svg"
            aria-hidden="true"
            alt=""
            render-without-figure
            lazy
            :width="67"
            :height="157"
          />
        </template>
      </SustainableTeaser>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Heading } from '@i22team/td-longtail-vue-components';
import CdnImage, { type CdnImageProps } from '~/components/cdn-image.vue';
import SustainableTeaser from './sustainable-teaser.vue';
import type { SustainablePromotion, SustainableTeaserType } from './sustainable-teaser-types';

import { useAsyncData } from '#imports';
import { getOfferByGroup, loadOffersForPromotions, type Promotion } from '~/lib/offers-by-group';

type TeaserPromotion = Promotion<SustainablePromotion>;

/**
 * Each teaser is a tile group with alternative article numbers (in order of priority).
 * Without any matching product, the teaser is not shown.
 * "image" is an optional manual image; without it, the Goliath product image is used.
 */
const fairbudsXlImage: CdnImageProps = {
  src: '/content/content-images/homepage/sustainable-products/30748a339166f21be4367c22972abf66/fairbuds-xl-2025.webp',
  alt: 'Fairphone Fairbuds XL schwarz',
  sizes: 'xs:80vw sm:80vw md:50vw lg:352px',
  aspectRatio: '1 / 1',
};
const fairphone6Image: CdnImageProps = {
  src: '/content/content-images/homepage/sustainable-products/e80b3f1f7d0c60fa4a686e78b86b112c/fairphone-rebranding-01-2025.png',
  srcMd:
    '/content/content-images/homepage/sustainable-products/f9b90fadfb1ab294aa6331d42c1acef5/fairphone-rebranding-01-2025@2x.png',
  alt: 'Fairphone 6 schwarz',
  sizes: 'xs:80vw sm:80vw md:50vw lg:352px',
  aspectRatio: '159 / 333',
};
const fairbudsV2Image: CdnImageProps = {
  src: '/content/content-images/homepage/sustainable-products/14f6cc21ac9246a6cbc6370fada9d6c5/fairphone-fairbuds-true-wireless-v2@2x.png',
  alt: 'Fairphone Fairbuds True Wireless Earbuds V2 weiß',
  sizes: 'xs:80vw sm:80vw md:50vw lg:352px',
  aspectRatio: '1 / 1',
};

const PROMOTIONS: TeaserPromotion[] = [
  // black
  { articleNumber: '8718819370536', tileGroups: ['fairbudsXl'], image: fairbudsXlImage },
  // Green
  { articleNumber: '8718819370543', tileGroups: ['fairbudsXl'], image: fairbudsXlImage },

  // Black
  {
    articleNumber: '8718819370888',
    tileGroups: ['fairphone6'],
    goliathImageProps: {
      sizes: 'xs:80vw sm:80vw md:50vw lg:352px',
      aspectRatio: '1',
    },
  },
  // Green
  { articleNumber: '99938109', tileGroups: ['fairphone6'] },
  // Blue
  { articleNumber: '8718819370895', tileGroups: ['fairphone6'] },

  // White
  { articleNumber: '8718819375890', tileGroups: ['fairbudsV2'], image: fairbudsV2Image },
  // Black
  { articleNumber: '8718819375883', tileGroups: ['fairbudsV2'], image: fairbudsV2Image },
];

const { data: promotionOffers } = await useAsyncData('sustainable-products', () =>
  loadOffersForPromotions<SustainablePromotion, ListOffer>(PROMOTIONS, false, false)
);

const teaser1 = computed<SustainableTeaserType | undefined>(() =>
  getOfferByGroup<SustainablePromotion>(promotionOffers.value, 'fairbudsXl', 1)
);

const teaser2 = computed<SustainableTeaserType | undefined>(() =>
  getOfferByGroup<SustainablePromotion>(promotionOffers.value, 'fairphone6', 1)
);

const teaser3 = computed<SustainableTeaserType | undefined>(() =>
  getOfferByGroup<SustainablePromotion>(promotionOffers.value, 'fairbudsV2', 1)
);

const hasTeasers = computed(() => teaser1.value || teaser2.value || teaser3.value);
</script>

<style scoped lang="scss">
@use 'assets/base' as *;

.sustainable-products {
  padding-inline: $grid-base;

  &__list {
    display: grid;
    gap: $grid-base;
    grid-template-columns: 1fr;

    @include mq($mq-sm) {
      grid-template-columns: 1fr 1fr;
      grid-gap: $grid-base * 2;
    }
    @include mq($mq-md) {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

.sustainable-teaser {
  &__container {
    position: relative;

    &-badge {
      z-index: 1;
      position: absolute;
      top: 2 * $grid-base;
      right: calc($grid-base / -2);
    }
  }

  &__element {
    transition: all 0.3s ease-in-out;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  &--rotate-right {
    rotate: 10deg;
  }
  &--rotate-left {
    rotate: -10deg;
  }
  &--move-left {
    transform: translateX(-10%);
  }
  &--move-right {
    transform: translateX(10%);
  }
}

.leaf-1,
.leaf-2 {
  transform-origin: left;
  justify-self: start;
  align-self: start;
}

.leaf-1 {
  margin-top: 1%;
}

.leaf-2 {
  margin-top: 20%;
}

.leaf-3,
.leaf-5 {
  transform-origin: right;
  justify-self: end;
  align-self: end;
}

.leaf-3 {
  margin-bottom: 10%;
}

.leaf-4,
.leaf-6 {
  transform-origin: left;
  justify-self: start;
  align-self: end;
}

.leaf-4 {
  margin-bottom: 30%;
}

.leaf-5 {
  margin-bottom: 10%;
}

.leaf-6 {
  margin-bottom: 5%;
}

.leaf-7,
.leaf-8 {
  transform-origin: top;
  justify-self: end;
  align-self: start;
}
.leaf-7 {
  margin-top: 10%;
}

.leaf-8 {
  margin-top: 10%;
}
</style>
